import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Brain, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const LIKERT_OPTIONS = [
  { value: '1', label: 'Strongly Agree' },
  { value: '2', label: 'Agree' },
  { value: '3', label: 'Neutral' },
  { value: '4', label: 'Disagree' },
  { value: '5', label: 'Strongly Disagree' }
];

export default function Assessment() {
  const { type } = useParams();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isExpress = type === 'express';

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await api.get(`/assessments/questions/${type}`);
        setQuestions(response.data.questions);
      } catch (error) {
        toast.error('Failed to load assessment');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [type, navigate]);

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isComplete = Object.keys(responses).length === questions.length;

  const handleResponse = (value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: parseInt(value)
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isComplete) {
      toast.error('Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/assessments/submit', {
        assessment_type: type,
        responses
      });
      
      toast.success('Assessment complete!');
      navigate('/report', { state: { newReport: true } });
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="assessment-page">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">
              {isExpress ? 'Quick Mind Scan' : 'Full Mind Diagnostic'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {questions.length} questions • {isExpress ? '~3 min' : '~15 min'}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-mono text-muted-foreground">
              Question {currentIndex + 1} of {questions.length}
            </span>
            <span className="font-mono text-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground mb-2">
                <span className="px-2 py-1 rounded bg-secondary">{currentQuestion?.domain?.replace(/_/g, ' ')}</span>
              </div>
              <CardTitle className="text-xl leading-relaxed font-medium">
                {currentQuestion?.text}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={responses[currentQuestion?.id]?.toString() || ''}
                onValueChange={handleResponse}
                className="space-y-3"
              >
                {LIKERT_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer
                      ${responses[currentQuestion?.id]?.toString() === option.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-border'
                      }`}
                    onClick={() => handleResponse(option.value)}
                  >
                    <RadioGroupItem value={option.value} id={`option-${option.value}`} />
                    <Label 
                      htmlFor={`option-${option.value}`} 
                      className="flex-1 cursor-pointer font-normal"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentIndex === 0}
          data-testid="prev-question"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>

        {currentIndex === questions.length - 1 ? (
          <Button
            onClick={handleSubmit}
            disabled={!isComplete || submitting}
            className="glow-primary"
            data-testid="submit-assessment"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Complete Assessment
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={handleNext}
            disabled={!responses[currentQuestion?.id]}
            data-testid="next-question"
          >
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Question Navigator */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <p className="text-xs font-mono text-muted-foreground mb-3">Question Navigator</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(index)}
                className={`h-8 w-8 rounded text-xs font-mono transition-all
                  ${index === currentIndex 
                    ? 'bg-primary text-primary-foreground' 
                    : responses[q.id] 
                      ? 'bg-emerald-500/20 text-emerald-500' 
                      : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                  }`}
                data-testid={`question-nav-${index}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
